import { useEffect, useRef, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { TimelineEvent } from '@/frontend/features/Fde/types'

export default function useTimelineCalendarEvents(projectId: string, initialMonth: string, initialEvents: TimelineEvent[]) {
  const [monthDate, setMonthDate] = useState(parseISO(initialMonth))
  const [events, setEvents] = useState(initialEvents)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const cache = useRef(new Map([[format(parseISO(initialMonth), 'yyyy-MM'), initialEvents]]))
  const request = useRef<AbortController | null>(null)

  useEffect(() => {
    const key = format(parseISO(initialMonth), 'yyyy-MM')
    request.current?.abort()
    request.current = null
    cache.current = new Map([[key, initialEvents]])
    setMonthDate(parseISO(initialMonth))
    setEvents(initialEvents)
    setLoading(false)
    setFailed(false)
  }, [projectId, initialMonth, initialEvents])

  useEffect(() => () => request.current?.abort(), [])

  async function changeMonth(nextMonth: Date) {
    const key = format(nextMonth, 'yyyy-MM')
    setMonthDate(nextMonth)
    setFailed(false)
    const cached = cache.current.get(key)
    if (cached) {
      setEvents(cached)
      return
    }

    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    setLoading(true)
    try {
      const response = await fetch(fdeProjectPath(fdeRoutes.timelineEvents, projectId, { month: `${key}-01` }), {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
      if (!response.ok) throw new Error(`Calendar request failed: ${response.status}`)
      const payload = await response.json() as { events: TimelineEvent[] }
      cache.current.set(key, payload.events)
      setEvents(payload.events)
    } catch (error) {
      if ((error as Error).name !== 'AbortError') setFailed(true)
    } finally {
      if (request.current === controller) setLoading(false)
    }
  }

  return { monthDate, events, loading, failed, changeMonth }
}
